import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { Set } from 'immutable';
import { Checkbox } from '@components/FormInputs';
import styles from '../VirtualizedList.module.scss';
import { useDraggable } from '@dnd-kit/core';
import { useContextMenu } from '@hooks/use-context-menu';
import { useIsOnScreen } from '@support/hooks/use-is-on-screen';

export default function ListRow({
  id,
  isMultiselect,
  children,
  style,
  isHighlighted,
  isSelected,
  isUpdating,
  isDeleting,
  isActiveList,
  highlightRow,
  onClick,
  onContextMenu,
  toggleRowOrGroupSelection,
  isLastToggledRow,
  listRowClassName,
  itemBasedListRowClassName,
  disabled,
  selectedItems,
  canDrag,
  contextMenuTarget,
  contextMenuDisabled,
  scrollIntoView,
}) {
  // A highlighted row may re-render -- we want to know if it was highlighted on first render
  const wasHighlightedOnMount = useRef(isHighlighted);
  const { listeners, setNodeRef, node } = useDraggable({
    id,
    data: {
      // For now we only support dragging of contactIDs, but we could parameterize this
      // if we want to support dragging of other data types from lists
      type: 'contactIDs',
      contactIDs: isSelected ? selectedItems : Set([id]),
    },
    disabled: !canDrag,
  });
  const isOnScreen = useIsOnScreen(node, { rootMargin: '-87px 0px -50px 0px' });

  const handleContextMenu = useContextMenu({
    target: contextMenuTarget,
    handler: onContextMenu,
    disabled: contextMenuDisabled,
  });

  if (listeners && listeners?.keyDown) {
    listeners.keyDown = undefined;
  }

  const [checkboxIsHovered, setCheckboxIsHovered] = useState(false);
  // Warning: hacks ahead.
  // In Safari, a mouseMove event can be/is fired when a user's mouse is in an
  // element that has been re-rendered, even if the mouse doesn't actually move.
  // This wreaks havoc on shift arrow selection if the mouse cursor is over the list
  // when the selection event begins. If it is, the hovered row item will receive
  // the highlight state when the selection event ends. Waiting for moveCount to increment
  // to 1 sometimes solves this problem, but using 2 seems to be a much higher percentage
  // solve.
  const [moveCount, setMoveCount] = useState(0);

  const handleMouseMove = (e) => {
    if (disabled) {
      return;
    }

    // If user is trying to get to sidebar, don't highlight
    if ((e.movementX || 0) > 0) {
      if (Math.hypot(e.movementX || 0, e.movementY || 0) > 4) {
        return;
      }
    }

    if (moveCount < 2) {
      return setMoveCount(moveCount + 1);
    }

    if (!isHighlighted) {
      highlightRow(e.shiftKey);
    }
  };

  useEffect(() => {
    setMoveCount(0);
  }, [isHighlighted]);

  useEffect(() => {
    if (isHighlighted && !wasHighlightedOnMount.current && !isOnScreen) {
      scrollIntoView();
    }
  }, [isHighlighted]);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        styles.item,
        isSelected && styles.selected,
        isHighlighted && styles.highlighted,
        isDeleting && styles.deleting,
        isUpdating && styles.updating,
        !isMultiselect && styles.noCheckbox,
        disabled && styles.disabled,
        !isActiveList && styles.listInactive,
        listRowClassName,
        itemBasedListRowClassName && itemBasedListRowClassName(id),
        isHighlighted && 'highlighted',
      )}
      style={style}
      onClick={onClick}
      onContextMenu={handleContextMenu}
      onMouseMove={handleMouseMove}
      {...(listeners || {})}
    >
      {isMultiselect && (
        <div
          className={cn(
            styles.checkboxContainer,
            isLastToggledRow && styles.lastToggled,
            (isHighlighted || isSelected || isLastToggledRow) && styles.showCheckbox,
            disabled && styles.disabled,
          )}
          onClick={toggleRowOrGroupSelection}
          onMouseEnter={() => setCheckboxIsHovered(true)}
          onMouseLeave={() => setCheckboxIsHovered(false)}
        >
          <Checkbox
            id={id}
            checked={isSelected}
            onChange={() => {}}
            onClick={(e) => e.currentTarget.blur()}
            tabIndex={-1}
            focused={isLastToggledRow}
            hovered={checkboxIsHovered}
          />
        </div>
      )}
      {children}
    </div>
  );
}
